import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmailPreview } from "@/components/admin/EmailPreview";
import { useEmailTemplate } from "@/components/admin/EmailTemplateEditor";
import { adminContent } from "@/i18n/admin";
import { useLang, type Lang } from "@/i18n/language";
import { supabase } from "@/integrations/supabase/client";
import { renderTemplate } from "@/lib/emailTemplate";
import { formatDate } from "@/lib/utils";

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const btnPrimary =
  "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50";

export type EmailRecipient = { name: string; email: string | null };

export function EmailComposer({
  recipients,
  groupId,
  applicationId,
  groupName,
  startsOn,
}: {
  recipients: EmailRecipient[];
  groupId?: string | undefined;
  applicationId?: string | undefined;
  groupName?: string | undefined;
  startsOn?: string | undefined;
}) {
  const { lang } = useLang();
  const t = adminContent[lang].email;
  const templateQuery = useEmailTemplate();
  const template = templateQuery.data ?? null;

  const [open, setOpen] = useState(false);
  const [emailLang, setEmailLang] = useState<Lang>(lang);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  const emails = recipients.map((r) => r.email).filter((e): e is string => Boolean(e));
  const missing = recipients.length - emails.length;
  const singleName = recipients.length === 1 ? (recipients[0]?.name ?? "") : "";

  const vars = {
    nombre: singleName || (emailLang === "es" ? "participante" : "participant"),
    grupo: groupName ?? "",
    fecha: startsOn ? formatDate(startsOn) : "",
    enlace: link,
  };

  // Refresh the editable copy from the saved template unless the admin edited it.
  useEffect(() => {
    if (!template || touched) return;
    const rendered = renderTemplate(template, emailLang, vars);
    setSubject(rendered.subject);
    setBody(rendered.body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, emailLang, touched, link, groupName, startsOn, singleName]);

  async function send() {
    if (!subject.trim() || !body.trim()) {
      toast.error(t.missingFields);
      return;
    }
    if (emails.length === 0) {
      toast.error(t.noRecipients);
      return;
    }
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("email_messages").insert({
      group_id: groupId ?? null,
      application_id: applicationId ?? null,
      template_id: template?.id ?? null,
      lang: emailLang,
      recipients: emails,
      subject: subject.trim(),
      body: body.trim(),
      session_link: link.trim() ? link.trim() : null,
      delivery: "demo",
      created_by: auth.user?.id ?? null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.demoSent.replace("{n}", String(emails.length)));
    setOpen(false);
    setTouched(false);
    setLink("");
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={btnGhost}>
        {groupId && !applicationId ? t.openGroup : t.openIndividual}
      </button>
    );
  }

  const buttonLabel = template
    ? emailLang === "es"
      ? template.button_label_es
      : template.button_label_en
    : "";
  const signature = template
    ? emailLang === "es"
      ? template.signature_es
      : template.signature_en
    : "";

  return (
    <div className="mt-4 w-full rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold tracking-widest uppercase text-primary">{t.title}</h4>
        <div className="flex items-center gap-2">
          {(["es", "en"] as const).map((key) => (
            <button
              key={key}
              onClick={() => {
                setTouched(false);
                setEmailLang(key);
              }}
              className={emailLang === key ? btnPrimary : btnGhost}
            >
              {key.toUpperCase()}
            </button>
          ))}
          <span className="rounded-full border border-primary/40 px-2.5 py-0.5 text-[10px] font-semibold tracking-widest uppercase text-primary">
            {t.demoBadge}
          </span>
        </div>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{t.demoNote}</p>

      <p className="mt-3 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground/80">{t.to}: </span>
        {emails.length ? emails.join(", ") : "—"}
        {missing > 0 ? ` · ${t.missingEmails.replace("{n}", String(missing))}` : ""}
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground">{t.subject}</label>
          <input
            value={subject}
            onChange={(e) => {
              setTouched(true);
              setSubject(e.target.value);
            }}
            className={`${inputClass} mt-1.5`}
          />

          <label className="mt-3 block text-xs font-semibold text-muted-foreground">
            {t.sessionLink}
          </label>
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://meet.google.com/…"
            className={`${inputClass} mt-1.5`}
          />

          <label className="mt-3 block text-xs font-semibold text-muted-foreground">{t.body}</label>
          <textarea
            value={body}
            onChange={(e) => {
              setTouched(true);
              setBody(e.target.value);
            }}
            rows={9}
            className={`${inputClass} mt-1.5 resize-y`}
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground">{t.variablesHint}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={send} disabled={busy} className={btnPrimary}>
              {t.send}
            </button>
            <button onClick={() => setOpen(false)} disabled={busy} className={btnGhost}>
              {t.cancel}
            </button>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            {t.preview}
          </p>
          <div className="mt-2">
            <EmailPreview
              subject={subject}
              body={body}
              buttonLabel={buttonLabel}
              signature={signature}
              headerImage={template?.header_image_url ?? null}
              link={link.trim() ? link.trim() : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
